import { faMinus as lightMinus } from "@jewlr/pro-light-svg-icons/faMinus"
import { faPlus as lightPlus } from "@jewlr/pro-light-svg-icons/faPlus"
import {
  Box,
  Button,
  Flex,
  FontAwesomeIcon,
  Image,
  Typography,
} from "@jewlr/storybook/core"
import PropTypes from "prop-types"
import React, { useEffect, useRef } from "react"
import { connect } from "react-redux"
import scroll from "scroll"
import scrollDoc from "scroll-doc"
import styled from "styled-components"

import { fetchSize } from "areas/product/jewlr/modals/helper"
import {
  SizeBoxSmall,
  Size,
} from "areas/product/jewlr/modals/styled-components"
import {
  clearDefaultEngravings,
  scrollPanelIndex,
  togglePanelIndex,
} from "areas/product/store/actions"
import { DividerText } from "components/common"
import { builderImage } from "helpers/product/builder-image"
import displayOptionDescription from "helpers/product/display-option-description"
import displayOptionPrice from "helpers/product/display-option-price"
import { imageLookup } from "helpers/product/image-lookup"

import { SkuIcon } from "./components"

const MetalIcon = styled(SkuIcon)`
  font-size: 14px;
  height: 40px;
  margin: 0 16px 0 0;
  width: 40px;
`
const StyledTitleDetails = styled(Box).attrs((props) => ({
  ...props,
}))`
  align-items: flex-start;
  display: flex;
  flex-direction: column;
`

const AccordionThumbImg = styled(Image).attrs((props) => ({
  height: "40px",
  mr: { _: "16px", tablet: "15px" },
  width: "40px",
  ...props,
}))`
  margin-right: 16px;
  ${(props) => props.theme.mediaQueries.tablet`
    margin-right: 15px;
  `}
`

const AccordionHeadingBox = styled(Box)`
  border-top: 1px solid ${(props) => props.theme.colors.grey[10]};
  padding: 4px;
`

const AccordionHeadingButton = styled(Button).attrs({
  pl: "6px",
  pr: { _: "14px", tablet: "6px" },
  py: "6px",
  variant: "text",
})`
  display: flex;
  justify-content: space-between;
  text-align: left;
  width: 100%;
  ${(props) =>
    props.firstPref &&
    props.theme.mediaQueries.mobileOnly`
      &:focus {
        border-radius: 20px 20px 0 0;
      }
    `}
  ${(props) =>
    props.lastPref &&
    !props.isOpened &&
    props.theme.mediaQueries.mobileOnly`
      &:focus {
        border-radius: 0 0 20px 20px;
      }
    `}
`

const AccordionHeadingTitle = styled(Typography)`
  color: #313131;
  font-size: 14px;
  font-weight: 700;
  letter-spacing: 0.5px;
  margin: 0;
  text-transform: uppercase;
`

const AccordionHeadingDetails = styled(Typography)`
  color: ${(props) => props.theme.colors.grey65};
  display: inline-block;
  font-size: 12px;
  font-weight: normal;
  letter-spacing: 0.5px;
  margin: 0;
  width: ${(props) => (props.hasSizeTips ? "100%" : "auto")};
`

const AccordionHeadingPrice = styled(Typography)`
  font-size: 14px;
  font-weight: 700;
  letter-spacing: 0.5px;
  margin: 0 20px 0 0;
`

const PlusMinus = styled(FontAwesomeIcon)`
  color: #646464;
  font-size: 16px;
  transition: all 0.2s ease;
`

const MobileSeperator = styled(Typography).attrs({
  as: "div",
})`
  padding: 25px 20px;
  background-color: ${(props) => props.theme.colors.grey4};
  border-top: 1px solid ${(props) => props.theme.colors.grey10};
`

const Separator = styled(DividerText)`
  color: ${(props) => props.theme.colors.black};
  font-size: 14px;
  font-weight: 700;
  margin: 0;
  text-transform: uppercase;
  span {
    background-color: ${(props) => props.theme.colors.grey4};
  }
`

const DesktopSeperator = styled(Typography).attrs({
  as: "h2",
})`
  padding: 0 10px 5px;
  font-size: 20px;
  letter-spacing: 2px;
  text-transform: uppercase;
  border-bottom: 2px solid #a0a0a0;
  font-weight: bold;
  @media (min-width: 768px) {
    padding: 0;
    /* padding: 10px 10px 5px 10px; */
    margin: 0.83em 0;
  }
`

const InternalAccordionHeading = ({
  bandColor,
  dataRing,
  description,
  hideMiniview,
  hidePrice,
  image,
  isOpened,
  pref,
  prefOrder,
  price,
  removeScroll,
  scrollPanel,
  selected,
  togglePanel,
  wizardData,
}) => {
  const headingRef = useRef()

  const wizardDataHeading = wizardData && wizardData.heading

  const doScroll = () => {
    const page = scrollDoc()
    let pageOffset = hideMiniview ? 80 : 300
    const openedHeading = headingRef.current
    const yOffset = openedHeading.offsetTop - pageOffset
    scroll.top(page, yOffset)
    removeScroll()
  }

  useEffect(() => {
    if (scrollPanel === pref) {
      doScroll()
    }
  }, [scrollPanel, pref])

  const renderThumb = () => {
    if (selected && dataRing && !dataRing.not_a_ring) {
      var sizeString = fetchSize(selected.option_description)
      if (selected.value === "") {
        return (
          <SizeBoxSmall selected={false} title="Size">
            <Size />
          </SizeBoxSmall>
        )
      }
      return (
        <SizeBoxSmall selected={false} title="Size">
          <Size
            dangerouslySetInnerHTML={{
              __html: sizeString,
            }}
          />
        </SizeBoxSmall>
      )
    } else if (/b[0-9]+/.test(wizardDataHeading.pref)) {
      return (
        <div
          style={{
            border: "5px solid rgb(" + bandColor + ")",
            borderRadius: "50%",
            height: "35px",
            marginLeft: "7px",
            marginRight: "16px",
            width: "35px",
          }}
        />
      )
    } else if (
      (wizardDataHeading.pref === "sku" ||
        /m[0-9]+/.test(wizardDataHeading.pref)) &&
      selected
    ) {
      if (selected.value === "CER") {
        return <MetalIcon skuColor="CE">CE</MetalIcon>
      } else if (selected.value === "VERMY") {
        return <MetalIcon skuColor="V">V</MetalIcon>
      } else if (selected.value === "SS14KYP") {
        return <MetalIcon skuColor="YGP">14K</MetalIcon>
      } else if (selected.value === "SS18KYP") {
        return <MetalIcon skuColor="YGP">18K</MetalIcon>
      } else if (selected.value === "SSRGP") {
        return <MetalIcon skuColor="R">R</MetalIcon>
      } else {
        return (
          <MetalIcon skuColor={selected.imageKey?.slice(-1)}>
            {selected.icon_text}
          </MetalIcon>
        )
      }
    } else if (image && image.x1) {
      return (
        <AccordionThumbImg
          alt={description || ""}
          data-sizes="40px"
          src={image.x1}
          srcSet={image.x1 + " 1x, " + image.x2 + " 2x"}
        />
      )
    } else if (typeof image === "string") {
      return (
        <AccordionThumbImg
          alt={description || ""}
          data-sizes="40px"
          src={image}
        />
      )
    } else {
      return (
        <AccordionThumbImg
          alt={description || ""}
          data-sizes="40px"
          src={require("images/icons/jewlr.gif")}
        />
      )
    }
  }

  if (
    !wizardDataHeading ||
    wizardDataHeading.disabled ||
    (/^s[0-9]+/.test(pref) &&
      wizardData.options.length <= 1 &&
      !wizardData.override_text)
  ) {
    return null
  }

  return (
    <>
      {/^face[0-9]+$/.test(wizardDataHeading.pref) && (
        // added face option header
        <div>
          <DesktopSeperator display={{ _: "none", tablet: "block" }}>
            {"SIDE " + wizardDataHeading.pref.replace(/\D/g, "")}
          </DesktopSeperator>

          <MobileSeperator display={{ tablet: "none" }}>
            <Separator>
              <span>{"SIDE " + wizardDataHeading.pref.replace(/\D/g, "")}</span>
            </Separator>
          </MobileSeperator>
        </div>
      )}
      <AccordionHeadingBox bg={isOpened ? "polar" : "white"} ref={headingRef}>
        <Box as="h2" m="0">
          <AccordionHeadingButton
            aria-controls={`wizard-accordion-content-${wizardDataHeading.pref}`}
            aria-expanded={isOpened}
            data-cy={`product-wizard-heading-${wizardDataHeading.pref}`}
            firstPref={prefOrder[0] === pref}
            id={`wizard-accordion-heading-${wizardDataHeading.pref}`}
            isOpened={isOpened}
            lastPref={prefOrder[prefOrder.length - 1] === pref}
            onClick={() => togglePanel(wizardDataHeading.pref)}
          >
            <Flex>
              {renderThumb()}
              <StyledTitleDetails
                justifyContent={
                  wizardDataHeading.pref === "diamond" && "center"
                }
              >
                <AccordionHeadingTitle>
                  {wizardDataHeading.title}
                </AccordionHeadingTitle>
                <AccordionHeadingDetails
                  data-cy={`product-wizard-heading-details-${wizardDataHeading.pref}`}
                  hasSizeTips={wizardDataHeading.ring_size_tips}
                >
                  {description}
                </AccordionHeadingDetails>
              </StyledTitleDetails>
            </Flex>

            <Flex alignItems="center">
              {!hidePrice && (
                <AccordionHeadingPrice>{price}</AccordionHeadingPrice>
              )}
              {isOpened ? (
                <PlusMinus icon={lightMinus} />
              ) : (
                <PlusMinus icon={lightPlus} />
              )}
            </Flex>
          </AccordionHeadingButton>
        </Box>
      </AccordionHeadingBox>
    </>
  )
}

const getBandColor = (state, ownProps) => {
  if (/b[0-9]+/.test(ownProps.pref)) {
    return state.choices[ownProps.pref].rgb
  }
}
const getRingData = (state, ownProps) => {
  if (ownProps.pref === "sz") {
    return state.wizard.data.sz
  } else {
    return null
  }
}

const getImage = (wizardData, state, ownProps) => {
  if (!wizardData) {
    return null
  }
  if (wizardData.view_type == "no-image") {
    return {
      x1: "",
      x2: "",
    }
  }
  let choiceData = state.choices[wizardData.pref]
  if (ownProps.pref == "diamond") {
    choiceData = state.choices.d_shape
  }
  if (/c[0-9]+$/.test(wizardData.pref)) {
    return builderImage(wizardData.pref, state.choices)
  } else if (wizardData.heading?.pref === "diamond") {
    const finalDiamond = state.diamondSelector.selected_diamond || null
    if (finalDiamond?.shape) {
      let shapeCode = finalDiamond.shape.toLowerCase()
      let stoneType = finalDiamond.stone_type
      return {
        x1: `${sl.config.jewlr_asset_url}images/product/diamond/${shapeCode}/${stoneType}.png`,
        x2: `${sl.config.jewlr_asset_url}images/product/diamond/${shapeCode}/${stoneType}_2x.png`,
      }
    } else {
      return {
        x1: `${sl.config.jewlr_asset_url}images/product/diamond/rd/04DIAM.png`,
        x2: `${sl.config.jewlr_asset_url}images/product/diamond/rd/04DIAM_2x.png`,
      }
    }
  } else if (choiceData && typeof choiceData.imageKey !== "undefined") {
    return imageLookup(wizardData.pref, choiceData.imageKey)
  } else if (
    typeof choiceData === "undefined" ||
    !choiceData.image ||
    wizardData.pref == "ch" ||
    /ch[0-9]+$/.test(wizardData.pref) ||
    wizardData.pref == "box"
  ) {
    return {
      x1: require("images/icons/jewlr.gif"),
      x2: require("images/icons/jewlr.gif"),
    }
  } else if (typeof choiceData.image.x2 === "undefined") {
    return {
      x1: choiceData.image,
      x2: choiceData.image,
    }
  } else {
    return choiceData.image
  }
}

const mapStateToProps = (state, ownProps) => {
  let wizardData = null
  wizardData = state.wizard.data[ownProps.pref]
  return {
    bandColor: getBandColor(state, ownProps),
    dataRing: getRingData(state, ownProps),
    description: displayOptionDescription(wizardData, state, ownProps),
    hideMiniview: state.previewImageUI.miniview.scrollHide,
    hidePrice: state.wizard.metalsIncludeSurchargeFor.includes(ownProps.pref),
    image: getImage(wizardData, state, ownProps),
    isOpened: state.panel.openedPanelIndexes.indexOf(ownProps.pref) > -1,
    prefOrder: state.wizard.prefOrder,
    prefStore: state.wizardUI.prefStore,
    price: displayOptionPrice(state, ownProps),
    scrollPanel: state.panel.scrollPanelIndex,
    selected: state.choices[ownProps.pref],
    wizardData,
  }
}

const mapDispatchToProps = (dispatch, ownProps) => {
  return {
    removeScroll: () => {
      dispatch(scrollPanelIndex(""))
    },
    togglePanel: (panel) => {
      if (panel == "ef" || panel == "ef2") {
        dispatch(clearDefaultEngravings())
      }
      dispatch(togglePanelIndex(panel, ownProps.pref))
    },
  }
}

InternalAccordionHeading.propTypes = {
  bandColor: PropTypes.string,
  dataRing: PropTypes.object,
  description: PropTypes.string,
  hideMiniview: PropTypes.bool,
  hidePrice: PropTypes.bool,
  image: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  isOpened: PropTypes.bool,
  pref: PropTypes.string,
  prefOrder: PropTypes.array,
  price: PropTypes.string,
  removeScroll: PropTypes.func,
  scrollPanel: PropTypes.string,
  selected: PropTypes.object,
  togglePanel: PropTypes.func,
  wizardData: PropTypes.object,
}

const AccordionHeading = connect(
  mapStateToProps,
  mapDispatchToProps
)(InternalAccordionHeading)

export default AccordionHeading
